
import { auth, db, firebaseConfig } from './firebaseConfig';
import { initializeApp, deleteApp, getApp } from 'firebase/app';
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signOut as firebaseSignOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  User as FirebaseUser,
  getAuth
} from 'firebase/auth';
import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  collection, 
  getDocs,
  deleteDoc,
  query,
  where,
  serverTimestamp
} from 'firebase/firestore';
import { User, UserProfileType } from '../types';

const getTodayString = () => new Date().toISOString().split('T')[0];

const mapUserDoc = (id: string, data: any): User => {
  return {
    id: id,
    email: data.email || '',
    name: data.name || data.email?.split('@')[0] || 'Usuário',
    profile: (data.profile as UserProfileType) || UserProfileType.FREE,
    requestsToday: data.requestsToday || 0,
    lastRequestDate: data.lastRequestDate || getTodayString(),
    subscriptionExpiry: data.subscriptionExpiry || null,
    createdAt: data.createdAt || new Date().toISOString()
  };
};

const registerProfileForExistingUser = async (firebaseUser: FirebaseUser, customData?: { name?: string, profile?: UserProfileType }): Promise<User> => {
  const uid = firebaseUser.uid;
  const email = firebaseUser.email || '';
  
  let profile = customData?.profile || UserProfileType.FREE;
  const lowerEmail = email.toLowerCase();
  
  if (lowerEmail === 'caputo@caputoacademia.com.br' || lowerEmail.endsWith('@caputoacademia.com.br')) {
    profile = UserProfileType.MASTER;
  }

  // Regra de Expiração V2.1:
  // Todos os novos cadastros começam com 30 dias de teste (FREE).
  // Master e Ouro = Vitalício.
  const isLifetime = profile === UserProfileType.MASTER || profile === UserProfileType.OURO;
  const expiryDays = 30; 

  const newUser = {
    email: lowerEmail,
    name: customData?.name || email.split('@')[0],
    profile,
    requestsToday: 0,
    lastRequestDate: getTodayString(),
    subscriptionExpiry: isLifetime 
      ? null 
      : new Date(Date.now() + expiryDays * 24 * 60 * 60 * 1000).toISOString(),
    createdAt: new Date().toISOString()
  };

  await setDoc(doc(db, 'users', uid), newUser);
  return { id: uid, ...newUser };
};

export const subscribeToAuthChanges = (callback: (user: User | null) => void) => {
  return onAuthStateChanged(auth, async (firebaseUser) => {
    if (firebaseUser) {
      try {
        let userProfile = await getUserProfile(firebaseUser.uid);
        if (!userProfile) {
          userProfile = await registerProfileForExistingUser(firebaseUser);
        }

        if (userProfile) {
          const today = getTodayString();
          if (userProfile.lastRequestDate !== today) {
            try {
              await updateDoc(doc(db, 'users', firebaseUser.uid), {
                requestsToday: 0,
                lastRequestDate: today
              });
              userProfile.requestsToday = 0;
              userProfile.lastRequestDate = today;
            } catch (e) {}
          }
          callback(userProfile);
        } else {
          callback(null);
        }
      } catch (e) {
        callback(null);
      }
    } else {
      callback(null);
    }
  });
};

export const getUserProfile = async (uid: string): Promise<User | null> => {
  try {
    const docRef = doc(db, 'users', uid);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return mapUserDoc(uid, docSnap.data());
    }
    return null;
  } catch (error) {
    return null;
  }
};

export const login = async (email: string, password: string): Promise<User> => {
  const userCredential = await signInWithEmailAndPassword(auth, email.toLowerCase().trim(), password);
  let user = await getUserProfile(userCredential.user.uid);
  if (!user) user = await registerProfileForExistingUser(userCredential.user);
  return user;
};

export const register = async (email: string, password: string): Promise<User> => {
  const userCredential = await createUserWithEmailAndPassword(auth, email.toLowerCase().trim(), password);
  return await registerProfileForExistingUser(userCredential.user);
};

export const resetPassword = async (email: string): Promise<void> => {
  await sendPasswordResetEmail(auth, email.toLowerCase().trim());
};

export const logout = async () => {
  await firebaseSignOut(auth);
};

/**
 * Função de Verificação de Limites:
 * Agora bloqueia o acesso se a data de expiração passou (suspensão automática).
 */
export const checkLimits = (user: User): { allowed: boolean; error?: string } => {
  // Master e Ouro nunca expiram
  if (user.profile === UserProfileType.MASTER || user.profile === UserProfileType.OURO) {
    return { allowed: true };
  }

  // Verificação de Expiração (Prazos de 30 dias)
  if (user.subscriptionExpiry && new Date() > new Date(user.subscriptionExpiry)) {
    return { 
      allowed: false, 
      error: `Seu acesso ao plano ${user.profile} expirou. Por favor, realize o pagamento para renovar sua assinatura.` 
    };
  }

  const limits = {
    [UserProfileType.FREE]: 2,
    [UserProfileType.BRONZE]: 5,
    [UserProfileType.PRATA]: 15,
  };

  // @ts-ignore
  const limit = limits[user.profile] || 2;

  if (user.requestsToday >= limit) {
    return { 
      allowed: false, 
      error: `Limite diário de ${limit} protocolos atingido para o plano ${user.profile}. Faça upgrade para o Plano Ouro Vitalício e tenha acesso ilimitado!` 
    };
  }
  
  return { allowed: true };
};

export const getAllUsers = async (): Promise<User[]> => {
  const usersCol = collection(db, 'users');
  const querySnapshot = await getDocs(usersCol);
  const users: User[] = [];
  querySnapshot.forEach((doc) => {
    users.push(mapUserDoc(doc.id, doc.data()));
  });
  return users;
};

export const updateUserRole = async (userId: string, newProfile: UserProfileType) => {
  let newExpiry: string | null = null;
  
  // Se for Ouro ou Master, o acesso é vitalício (expiração = null)
  if (newProfile !== UserProfileType.OURO && newProfile !== UserProfileType.MASTER) {
      newExpiry = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();
  }

  await updateDoc(doc(db, 'users', userId), {
    profile: newProfile,
    subscriptionExpiry: newExpiry
  });
};

export const deleteUser = async (userId: string) => {
  await deleteDoc(doc(db, 'users', userId));
};

export const adminCreateUser = async (email: string, password: string, name: string, profile: UserProfileType): Promise<User> => {
  const secondaryAppName = `AdminAction-${Date.now()}`;
  const secondaryApp = initializeApp(firebaseConfig, secondaryAppName);
  const secondaryAuth = getAuth(secondaryApp);
  
  try {
    const userCredential = await createUserWithEmailAndPassword(secondaryAuth, email.toLowerCase().trim(), password);
    const newUser = await registerProfileForExistingUser(userCredential.user, { name, profile });
    
    await secondaryAuth.signOut();
    await deleteApp(secondaryApp);
    
    return newUser;
  } catch (error) {
    await deleteApp(secondaryApp);
    throw error;
  }
};
